import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import { Ellipsis, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { resolvePatternText, type PatternText } from '@/shared/patterns';
import type { ActionKind } from '@/shared/screen';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

/** Botões visíveis antes de "Mais ações" (U39 §6; `numberOfButtons={4}` no legado). */
export const MAX_VISIBLE_ACTIONS = 4;

/** Uma ação no lugar em que aparece: barra do detalhe, rodapé do lote ou "Mais ações". */
export interface ActionBarItem {
  readonly id: string;
  readonly label: PatternText;
  readonly kind: ActionKind;
  readonly icon?: LucideIcon | undefined;
  /** A ação principal da barra: botão cheio, por último na web e presa embaixo no iPhone. */
  readonly primary?: boolean | undefined;
  /** Rótulo curto da barra presa do iPhone ("Rejeitar" no lugar de "Rejeitar crédito", protótipo `m_actions_bar`). */
  readonly mobileLabel?: PatternText | undefined;
  /** Na web, só o ícone (o "Atualizar" do protótipo); o rótulo vira nome acessível e dica. */
  readonly iconOnly?: boolean | undefined;
  /** `true` desabilita; texto desabilita e explica o motivo. */
  readonly disabled?: boolean | PatternText | undefined;
  readonly onSelect: () => void;
}

/** Linha da tabela status × ação já avaliada (a forma de `VisibleAction` em `credit-note`). */
export interface ActionRow {
  readonly id: string;
  readonly kind: ActionKind;
  readonly disabled: boolean;
}

/** Como uma linha da tabela vira botão na tela: rótulo, ícone, primária e o que fazer. */
export type ActionBinding = Omit<ActionBarItem, 'id' | 'kind' | 'disabled'> & {
  /** Motivo mostrado quando a tabela devolve a linha desabilitada. */
  readonly disabledReason?: PatternText;
};

/**
 * Projeta a tabela status × ação (dado) na barra, na ordem da tabela. Linha sem `binding` fica de
 * fora e volta em `unbound`, para o teste da tela acusar ação esquecida.
 */
export function actionBarItems(rows: readonly ActionRow[], bindings: Readonly<Record<string, ActionBinding>>): { readonly items: ActionBarItem[]; readonly unbound: string[] } {
  const items: ActionBarItem[] = [];
  const unbound: string[] = [];
  for (const row of rows) {
    const binding = bindings[row.id];
    if (!binding) {
      unbound.push(row.id);
      continue;
    }
    const { disabledReason, ...rest } = binding;
    items.push({ ...rest, id: row.id, kind: row.kind, disabled: row.disabled ? (disabledReason ?? true) : false });
  }
  return { items, unbound };
}

/** Visíveis e o resto, com a primária por último entre as visíveis (protótipo `action_bar`). */
export function splitActions(items: readonly ActionBarItem[], max = MAX_VISIBLE_ACTIONS): { readonly visible: ActionBarItem[]; readonly more: ActionBarItem[] } {
  const shown = items.slice(0, max);
  const primary = shown.find((item) => item.primary === true);
  const visible = primary ? [...shown.filter((item) => item !== primary), primary] : shown;
  return { visible, more: items.slice(max) };
}

/** iPhone: a primária e no máximo mais uma na barra presa (protótipo `m_actions_bar`); o resto no sheet. */
export function splitMobileActions(items: readonly ActionBarItem[]): { readonly bar: ActionBarItem[]; readonly sheet: ActionBarItem[] } {
  const primary = items.find((item) => item.primary === true);
  const partner = items.find((item) => item !== primary && item.kind === 'destructive') ?? items.find((item) => item !== primary);
  const bar = [partner, primary].filter((item): item is ActionBarItem => item !== undefined);
  return { bar, sheet: items.filter((item) => !bar.includes(item)) };
}

const isDisabled = (item: ActionBarItem) => item.disabled !== undefined && item.disabled !== false;
const reasonOf = (item: ActionBarItem) => (typeof item.disabled === 'string' || typeof item.disabled === 'object' ? item.disabled : undefined);

function variantOf(item: ActionBarItem) {
  if (item.primary) return 'primary' as const;
  if (item.kind === 'destructive') return 'danger' as const;
  return 'secondary' as const;
}

function ActionButton({ item, size = 'default', iconOnly = false, short = false, className }: { readonly item: ActionBarItem; readonly size?: 'sm' | 'default' | 'lg'; readonly iconOnly?: boolean; readonly short?: boolean; readonly className?: string }) {
  const { i18n } = useLingui();
  const full = resolvePatternText(i18n, item.label);
  const label = short && item.mobileLabel ? resolvePatternText(i18n, item.mobileLabel) : full;
  const reason = reasonOf(item);
  const disabled = isDisabled(item);
  const Icon = item.icon;
  const button = (
    <Button
      type="button"
      variant={variantOf(item)}
      size={iconOnly ? 'icon' : size}
      aria-disabled={disabled || undefined}
      {...(iconOnly || label !== full ? { 'aria-label': full } : {})}
      data-action={item.id}
      className={className}
      onClick={() => {
        if (!disabled) item.onSelect();
      }}
    >
      {Icon ? <Icon aria-hidden /> : null}
      {iconOnly ? null : <span className="truncate">{label}</span>}
    </Button>
  );
  if (!reason && !iconOnly) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{reason ? resolvePatternText(i18n, reason) : full}</TooltipContent>
    </Tooltip>
  );
}

function MoreMenu({ items, size = 'default' }: { readonly items: readonly ActionBarItem[]; readonly size?: 'sm' | 'default' }) {
  const { i18n, t } = useLingui();
  if (items.length === 0) return null;
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" size={size === 'sm' ? 'sm' : 'icon'} aria-label={t`Mais ações`}>
              <Ellipsis aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t`Mais ações`}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent className="min-w-56">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem key={item.id} data-action={item.id} tone={item.kind === 'destructive' ? 'danger' : 'default'} disabled={isDisabled(item)} onSelect={() => item.onSelect()}>
              {Icon ? <Icon aria-hidden /> : null}
              {resolvePatternText(i18n, item.label)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function sheetLabel(i18n: I18n, item: ActionBarItem) {
  const reason = reasonOf(item);
  return { label: resolvePatternText(i18n, item.label), reason: reason ? resolvePatternText(i18n, reason) : undefined };
}

/** "Mais ações" do iPhone: bottom sheet com linhas de 52 px (protótipo `m_more`). */
export function MoreActionsSheet({ items, open, onOpenChange }: { readonly items: readonly ActionBarItem[]; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const { i18n, t } = useLingui();
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-slot="more-actions">
        <DrawerHeader>
          <DrawerTitle>{t`Mais ações`}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="pt-0 pb-4">
          <ul className="flex flex-col">
            {items.map((item) => {
              const Icon = item.icon;
              const { label, reason } = sheetLabel(i18n, item);
              const disabled = isDisabled(item);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    data-action={item.id}
                    disabled={disabled}
                    onClick={() => {
                      onOpenChange(false);
                      item.onSelect();
                    }}
                    className={cn(
                      'flex min-h-[52px] w-full items-center gap-3.5 text-left text-base disabled:cursor-not-allowed disabled:opacity-45',
                      item.kind === 'destructive' ? 'text-err' : 'text-foreground',
                    )}
                  >
                    {Icon ? <Icon aria-hidden className="size-[22px] shrink-0" /> : <span aria-hidden className="size-[22px] shrink-0" />}
                    <span className="flex min-w-0 flex-col">
                      <span>{label}</span>
                      {reason ? <span className="text-sm text-muted-foreground">{reason}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/** Barra presa embaixo no iPhone (U12): a primária maior, a parceira e o "…" para o sheet. */
export function MobileActionStrip({ items, children, className }: { readonly items: readonly ActionBarItem[]; readonly children?: ReactNode; readonly className?: string }) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const { bar, sheet } = splitMobileActions(items);
  if (items.length === 0 && !children) return null;
  return (
    <div
      data-slot="action-strip"
      className={cn(
        'sticky bottom-0 z-20 flex flex-col gap-2.5 border-t border-border bg-background px-3 pt-3 pb-3 shadow-3',
        className,
      )}
    >
      {children}
      {items.length > 0 ? (
        <div className="flex gap-2">
          {bar.map((item) => (
            <ActionButton key={item.id} item={item} size="lg" short className={cn('min-w-0 shrink basis-auto gap-1.5 px-3', item.primary ? 'grow-[2]' : 'grow')} />
          ))}
          {sheet.length > 0 ? (
            <>
              <Button type="button" variant="secondary" size="lg" aria-label={t`Mais ações`} className="shrink-0 px-3" onClick={() => setOpen(true)}>
                <Ellipsis aria-hidden />
              </Button>
              <MoreActionsSheet items={sheet} open={open} onOpenChange={setOpen} />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export interface ActionBarProps {
  readonly items: readonly ActionBarItem[];
  /** Botões visíveis na web antes de "Mais ações". */
  readonly max?: number;
  /** Nome acessível da barra ("Ações da operação"). */
  readonly label?: string;
  readonly className?: string;
}

/**
 * Barra de ações do detalhe (P10, U39 §6), dirigida pela tabela status × ação como dado
 * (`actionBarItems`). Web: até 4 botões, a primária por último e o resto em "Mais ações". iPhone: a
 * primária presa embaixo e as outras num bottom sheet.
 */
export function ActionBar({ items, max = MAX_VISIBLE_ACTIONS, label, className }: ActionBarProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  if (items.length === 0) return null;
  if (isMobile) {
    return (
      <div role="toolbar" aria-label={label ?? t`Ações`} className="contents">
        <MobileActionStrip items={items} {...(className ? { className } : {})} />
      </div>
    );
  }
  const { visible, more } = splitActions(items, max);
  return (
    <div role="toolbar" aria-label={label ?? t`Ações`} data-slot="action-bar" className={cn('flex flex-wrap items-center justify-end gap-2', className)}>
      <MoreMenu items={more} />
      {visible.map((item) => (
        <ActionButton key={item.id} item={item} iconOnly={item.iconOnly === true} />
      ))}
    </div>
  );
}

export { ActionButton as ActionBarButton, MoreMenu as MoreActionsMenu };
